import { FC, Fragment, useCallback, useContext, useMemo, useState } from "react";

import { Box, Stack } from "@mui/material";
import {
  FormType,
  TicketingFormCategory,
  formatCurrencyFromCents,
  getAddOnRates,
  isVariantActive,
} from "@simplyk/common";
import { useFieldArray } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../../../common/constants/amplitude";
import { useBurst } from "../../../../contexts/burstContext";
import { useFormV2Context } from "../../../../contexts/FormV2Context";
import { HeartsAnimationContext } from "../../../../contexts/heartsAnimationContext";
import { useLocaleField } from "../../../../helpers/useLocaleField";
import { useAuctionFirstStepSubmit } from "../../../../steps/step1/shared/CheckoutFooter/useAuctionFirstStepSubmit";
import { QuestionField } from "../../../questions/QuestionField/QuestionField";
import { AuctionDetailDrawer } from "../AuctionDetailDrawer/AuctionDetailDrawer";
import { useOccurrenceSelector } from "../OccurrenceSelector/OccurrenceSelectorContext";

import { AuctionRateItemContainer } from "./AuctionRateItemContainer";
import { calculateMaximumPurchasableTicketsForRate } from "./RateCard/helpers/calculateMaximumPurchasableTicketsForRate";
import { useTicketingV2CardState } from "./RateCard/hooks/useTicketingV2CardState";
import { ShopAdditionalQuestionsBlock } from "./ShopAdditionalQuestionsBlock/ShopAdditionalQuestionsBlock";
import { filterRatesByCategory } from "./SortByCategoryPills/helpers/filterRatesByCategory";
import { SortByCategoryPills } from "./SortByCategoryPills/SortByCategoryPills";
import { getRateTileId } from "./rateTileId.constant";
import { useAuctionDrawerState } from "./useAuctionDrawerState";

import { useShapes } from "@/canvas/components/BackgroundShapes/ShapesContext";
import { useShopCartContext } from "@/canvas/features/FormV2/blocks/payment/ShopCart/ShopCartContext";
import { MemberLoginDialog } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/MemberLoginDialog/MemberLoginDialog";
import { AdditionalQuestionsForTicket } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/AdditionalQuestionForTicket";
import { AdditionalQuestions } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/AdditionalQuestions";
import { AdditionalQuestionsForGroup } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/AdditionalQuestionsForGroup";
import { deriveDisplayUnitAmount } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/deriveDisplayUnitAmount";
import { MemberDuplicateWarning } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/MemberDuplicateWarning";
import { MemberInfoQuestions } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/MemberInfo/MemberInfoQuestions";
import { GroupMembershipMembers } from "./RateCard/MemberInfo/GroupMembershipMembers/GroupMembershipMembers";
import { allowsMultipleMembershipsForSelection } from "@/canvas/features/FormV2/helpers/membershipMemberInfo";
import { PayWhatYouCanInput } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/PayWhatYouCanInput";
import { RateCard } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/RateCard";
import { ShopPayWhatYouCanInput } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/ShopPayWhatYouCanInput";
import { InfiniteScroll } from "@/common/components/InfiniteScroll/InfiniteScroll";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { getPreviewPurchasedNumber } from "@/common/helpers/rates";
import { getShopParentCartQuantity } from "@/common/helpers/shopVariants";
import { usesEntryTerminology } from "@/common/helpers/sweepstakes";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FullRateError } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";

export { TICKETING_SELECTION_V2_RATE_ID_PREFIX } from "./rateTileId.constant";

const RATES_PAGE_SIZE = 20;
const EMPTY_FULL_RATES: FullRateError[] = [];

export const Rates: FC = () => {
  const { t } = useTranslate();
  const {
    formObject: ticketing,
    selectedOccurrenceId,
    selectedOccurrenceWithRates,
    hasADate,
    isTicketingClosed: ticketingIsClosed,
    questions,
  } = useFormV2Context(FormType.Ticketing);
  const fullRatesReturnByOrder: FullRateError[] = EMPTY_FULL_RATES;
  const {
    formCategory,
    form,
    isAuction,
    formCurrency,
    isP2PLinkedEvent,
    formObject,
    membershipInfo,
    memberInfo,
    validRatesSorted,
  } = useFormV2Context(FormType.Ticketing);

  const customAnswersOfParticipantsFieldArray = useFieldArray({
    control: form.control,
    name: "customAnswersOfParticipants",
  });

  const allRates = useMemo(() => ticketing.rates || [], [ticketing.rates]);
  const addOnRateIds = useMemo(() => new Set(getAddOnRates(allRates).map((rate) => rate.id)), [allRates]);

  const isShopForm = isVariantActive(formCategory);
  const { setOccurrenceSelectorForcedOpen } = useOccurrenceSelector();
  const getLocaleField = useLocaleField();
  const hasUnselectedOccurrence = !selectedOccurrenceId && hasADate;
  const isInteractionsDisabled = hasUnselectedOccurrence && !ticketingIsClosed; // Can interact with rate descriptions on closed ticketing
  const { rotate, reverseRotate } = useShapes();
  const { triggerHeartsAnimation } = useContext(HeartsAnimationContext);
  const { burstFromElement } = useBurst();
  const shopCart = useShopCartContext();
  const { logAmplitudeEvent } = useAmplitude();

  const { locale, isoLocale } = useLocaleContext();
  const { isPreview, currentStepKey } = usePreviewContext();
  const isGhostPreview = useIsGhostPreview("TicketingSelectionV2");
  const isQuestionsPreviewStep = isPreview && currentStepKey === "questions";
  const isQuestionsGhostPreview = useIsGhostPreview("TicketingSelectionV2CardQuestions");

  const itemType: "ticket" | "item" | "membership" =
    formCategory === TicketingFormCategory.MembershipV2
      ? "membership"
      : formCategory === TicketingFormCategory.Shop || formCategory === TicketingFormCategory.Custom
        ? "item"
        : "ticket";

  const rateCategories = ticketing.rateCategories ?? [];
  const { isReady, isRateAccessible, getDuplicateMembership, isMemberOnlyRate, isAuthenticated } = membershipInfo;
  const [memberLoginDialogOpen, setMemberLoginDialogOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const filteredValidRatesSorted = filterRatesByCategory(validRatesSorted ?? [], selectedCategoryId);

  const [visibleRatesCount, setVisibleRatesCount] = useState(RATES_PAGE_SIZE);
  const handleSelectCategory = useCallback((id: string | null) => {
    setSelectedCategoryId(id);
    setVisibleRatesCount(RATES_PAGE_SIZE);
  }, []);
  const visibleRatesSorted = isPreview
    ? filteredValidRatesSorted
    : filteredValidRatesSorted.slice(0, visibleRatesCount);
  const hasMoreRates = !isPreview && visibleRatesCount < filteredValidRatesSorted.length;
  const loadMoreRates = useCallback(() => {
    setVisibleRatesCount((count) => count + RATES_PAGE_SIZE);
  }, []);

  const {
    isOpen: isAuctionDrawerOpen,
    selectedRate: auctionDrawerSelectedRate,
    openDrawerForRate,
    closeDrawer: closeAuctionDrawer,
    goToNext: goToNextAuctionItem,
    goToPrevious: goToPreviousAuctionItem,
    hasNext: hasNextAuctionItem,
    hasPrevious: hasPreviousAuctionItem,
  } = useAuctionDrawerState(validRatesSorted ?? []);

  const { onFirstStepSubmit } = useAuctionFirstStepSubmit();

  const handleGoToCheckout = useCallback(() => {
    closeAuctionDrawer();
    void onFirstStepSubmit();
  }, [closeAuctionDrawer, onFirstStepSubmit]);

  const { allParticipants, generateOnIncrementAmount, generateOnDecrementAmount } = useTicketingV2CardState({
    participants: customAnswersOfParticipantsFieldArray,
    form,
    rates: validRatesSorted,
    allRates,
    questions,
    animations: {
      rotate,
      reverseRotate,
      triggerHearts: triggerHeartsAnimation,
    },
    callbacks: {
      onItemAmountChanged: ({ from, to, rate }) => {
        logAmplitudeEvent(AmplitudeEvents.DonorFormTicketsCountChanged, {
          formId: ticketing.id,
          formType: ticketing.formCategory,
          organizationCountry: ticketing.organization?.country,
          ticketsCountAfterChange: from,
          ticketsCountBeforeChange: to,
          id: rate.id,
          title: rate?.rateFields?.[0]?.title,
          amount: rate.amount,
          is_addon: Boolean(rate.isAddOn),
          change_type: to > from ? "increase" : "decrease",
        });
      },
    },
  });

  const selectedRateIds = useMemo(
    () => new Set(allParticipants.map((participant) => participant.rateId)),
    [allParticipants]
  );

  const handleClick = () => {
    if (hasUnselectedOccurrence) {
      setOccurrenceSelectorForcedOpen(true);
    }
  };

  const getDuplicateWarningNode = (rate: Parameters<typeof getDuplicateMembership>[0]) => {
    const dup = getDuplicateMembership(rate);
    if (!dup) {
      return undefined;
    }
    return <MemberDuplicateWarning expirationDate={dup.expirationDate} isAutoRenewal={dup.automaticRenewal} />;
  };

  // e.g. handleShopAddToCart(rate, 2, "tshirt-S-Red") → adds 2x child rate to ticketsPurchased
  const handleShopAddToCart = (
    rate: (typeof validRatesSorted)[number],
    quantity: number,
    childRateId: string | null
  ) => {
    // Fail closed if a shop rate with variants ever reaches here without resolving — never bind the donor to the parent rate.
    const hasVariants = (rate.childRatesInfo?.length ?? 0) > 0;
    if (isShopForm && !childRateId && hasVariants) {
      return;
    }
    const targetRateId = childRateId ?? rate.id;
    const attendeesByTicket = rate.attendeesByTicket || 1;
    const allPendingAnswers = form.getValues("shopPendingAnswers") ?? {};
    const pendingAnswersForRate = allPendingAnswers[rate.id] ?? [];

    const currentTickets = form.getValues("ticketsPurchased") ?? [];
    const isVariantChildLine = childRateId !== null && childRateId !== rate.id;
    const existingIndex = currentTickets.findIndex((ticket) => ticket.rateId === targetRateId);
    const existingLine = existingIndex >= 0 ? currentTickets[existingIndex] : null;

    const getPwycUnitAmount = (): number => {
      if (!rate.isPayWhatYouCan) {
        return 0;
      }
      if (isVariantChildLine) {
        const parentTicket = currentTickets.find((ticket) => ticket.rateId === rate.id);
        return parentTicket?.pwycAmount ?? 0;
      }
      if (!isShopForm) {
        return 0;
      }
      const prevQty = existingLine?.purchasedNumber ?? 0;
      const prevPwycAmount = existingLine?.pwycAmount ?? 0;
      return prevQty > 0 ? prevPwycAmount / prevQty : prevPwycAmount;
    };
    const pwycUnitAmount = getPwycUnitAmount();

    if (existingIndex >= 0) {
      const current = currentTickets[existingIndex].purchasedNumber ?? 0;
      const nextQty = current + quantity;
      form.setValue(`ticketsPurchased.${existingIndex}.purchasedNumber`, nextQty);
      if (pwycUnitAmount > 0) {
        form.setValue(`ticketsPurchased.${existingIndex}.pwycAmount`, pwycUnitAmount * nextQty);
      }
    } else {
      const newTicket: (typeof currentTickets)[number] = {
        rateId: targetRateId,
        purchasedNumber: quantity,
        attendeesByTicket,
      };
      if (pwycUnitAmount > 0) {
        newTicket.pwycAmount = pwycUnitAmount * quantity;
      }
      form.setValue("ticketsPurchased", [...currentTickets, newTicket]);
    }

    const currentParticipants = form.getValues("customAnswersOfParticipants") ?? [];
    // One participant per attendee × ticket; reuse the user's pending answers when available, else leave empty.
    const newParticipants = Array.from({ length: quantity * attendeesByTicket }, (_, idx) => {
      const pendingIdx = Math.floor(idx / attendeesByTicket);
      const pending = pendingAnswersForRate[pendingIdx];
      return {
        rateId: targetRateId,
        answers: pending?.answers ?? [],
      };
    });
    form.setValue("customAnswersOfParticipants", [...currentParticipants, ...newParticipants]);

    if (pendingAnswersForRate.length > 0) {
      const { [rate.id]: _consumed, ...rest } = allPendingAnswers;
      form.setValue("shopPendingAnswers", rest);
    }

    rotate();
    triggerHeartsAnimation();

    shopCart?.addToCart(rate.id, childRateId, quantity);
  };

  return (
    <>
      <Stack
        sx={{
          opacity: isGhostPreview ? GHOST_PREVIEW_OPACITY : undefined,
        }}
        data-test="ticketing-selection-v2"
        direction="column"
        gap={2}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <Box onClick={handleClick}>
          <Stack
            inert={isInteractionsDisabled}
            sx={{
              pointerEvents: isInteractionsDisabled || isGhostPreview ? "none" : "auto", // Disable interactions for inner content only
              opacity: ticketingIsClosed || !hasUnselectedOccurrence || isPreview ? 1 : 0.4,
              "& > [aria-hidden]:last-child:not(:first-child)": { marginTop: -2 },
            }}
            gap={2}
          >
            <SortByCategoryPills
              categories={rateCategories}
              selectedCategoryId={selectedCategoryId}
              onSelect={handleSelectCategory}
            />
            <InfiniteScroll hasMore={hasMoreRates} onLoadMore={loadMoreRates} isLoading={false}>
              {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
              {visibleRatesSorted.map((rate, index) => {
                const titleAndDescription = getLocaleField(rate.rateFields);
                const isPayWhatYouCan = Boolean(rate.isPayWhatYouCan);

                if (!titleAndDescription) {
                  return null;
                }

                const rateQuestions = questions.filter(
                  (q) => q.hasRate && q?.rateQuestions?.some((rq) => rq.rateId === rate.id)
                );
                const forceRatePreview = isQuestionsPreviewStep && rateQuestions.length > 0;

                const previewPurchasedNumber = getPreviewPurchasedNumber(rate);
                const participantsForRate = forceRatePreview
                  ? Array.from({ length: previewPurchasedNumber * (rate.attendeesByTicket || 1) }, (_unused, i) => ({
                      id: String(i + 1),
                    }))
                  : allParticipants.filter((p) => p.rateId === rate.id);
                const purchasedNumber = forceRatePreview
                  ? previewPurchasedNumber
                  : rate.groupMembershipMaxMembers
                    ? new Set(
                        allParticipants
                          .filter((participant) => participant.rateId === rate.id)
                          .map((participant) => participant.membershipGroupIndex ?? 0)
                      ).size
                    : participantsForRate.length / (rate.attendeesByTicket || 1);

                const totalPurchasedUnits = new Set(
                  allParticipants
                    .filter((p) => !addOnRateIds.has(p.rateId))
                    .map((participant) =>
                      participant.membershipGroupIndex === undefined
                        ? participant.id
                        : `${participant.rateId}:${participant.membershipGroupIndex}`
                    )
                ).size;
                const totalParticipants = allParticipants.filter((p) => !addOnRateIds.has(p.rateId)).length;

                // Total sold tickets for the selected occurrence
                const totalSoldTickets =
                  selectedOccurrenceWithRates?.rates.reduce(
                    (acc, r) => (addOnRateIds.has(r.rateId) ? acc : acc + (r.soldTicketsCount || 0)),
                    0
                  ) || 0;

                const eventCapacity = ticketing?.seats || Infinity;

                // Only allow one membership per purchase or one ticket for P2P-linked event forms
                const isSingleMembershipPurchase =
                  itemType === "membership" && !allowsMultipleMembershipsForSelection(allRates, rate, selectedRateIds);
                const remainingTicketingSeats =
                  isSingleMembershipPurchase || isP2PLinkedEvent ? 1 : Math.max(eventCapacity - totalSoldTickets, 0);
                const currentRateByOccurrence = selectedOccurrenceWithRates?.rates.find((r) => r.rateId === rate.id);
                const remainingMemberCapacity = Math.max(
                  0,
                  Math.min(
                    eventCapacity - totalSoldTickets - totalParticipants,
                    (currentRateByOccurrence?.remainingTickets ?? Infinity) - participantsForRate.length
                  )
                );

                const {
                  maximumPurchasableTicketsForRate,
                  maximumPurchasableTicketsForRateAndEventCapacity,
                  minRateCapacityReason: rateSoldOutReason,
                } = calculateMaximumPurchasableTicketsForRate({
                  rate,
                  purchasedNumber,
                  totalParticipants:
                    isSingleMembershipPurchase || isP2PLinkedEvent ? totalPurchasedUnits : totalParticipants,
                  remainingTicketingSeats,
                  isSinglePurchaseLimited: isSingleMembershipPurchase || isP2PLinkedEvent,
                  currentRateByOccurrence,
                  fullRatesReturnByOrder,
                  groupMembershipSelection: rate.groupMembershipMaxMembers
                    ? {
                        selectedMembers: participantsForRate.length,
                        totalMembers: totalParticipants,
                        remainingEventSeats: Math.max(eventCapacity - totalSoldTickets, 0),
                      }
                    : undefined,
                });

                const isRateSoldOut = maximumPurchasableTicketsForRate <= 0;

                const ticketsPurchasedSnapshot = isShopForm ? form.watch("ticketsPurchased") : undefined;
                const hasShopVariantChildren = isShopForm && (rate.childRatesInfo?.length ?? 0) > 0;
                const shopCartQuantity =
                  hasShopVariantChildren && ticketsPurchasedSnapshot
                    ? getShopParentCartQuantity(rate, ticketsPurchasedSnapshot)
                    : 0;
                const isActive = purchasedNumber > 0 || shopCartQuantity > 0;

                const usesInlineShopPwyc = isShopForm && isPayWhatYouCan;
                const parentTicketIndex = ticketsPurchasedSnapshot?.findIndex((t) => t.rateId === rate.id) ?? -1;
                const pwycMin = rate.payWhatYouCanMinimumAmount ?? 0;
                const isPwycCartLocked = usesInlineShopPwyc && shopCartQuantity > 0;

                const duplicateWarning = getDuplicateWarningNode(rate);

                const tileId = getRateTileId(rate.id);
                const burstFromTileCounter = () => {
                  requestAnimationFrame(() => {
                    const tile = document.getElementById(tileId);
                    const counterValue = tile?.querySelector<HTMLElement>('[data-test="themed-counter-value"]');
                    burstFromElement(counterValue ?? tile);
                  });
                };

                const onIncrement = () => {
                  if (isMemberOnlyRate(rate) && !isReady) {
                    return;
                  }
                  if (isMemberOnlyRate(rate) && !isAuthenticated) {
                    setMemberLoginDialogOpen(true);
                    return;
                  }
                  const didIncrement = generateOnIncrementAmount({
                    rateId: rate.id,
                    title: titleAndDescription.title,
                    remainingMemberCapacity: rate.groupMembershipMaxMembers ? remainingMemberCapacity : undefined,
                  })();
                  if (didIncrement) {
                    burstFromTileCounter();
                  }
                };

                const isMemberOnlyAndNotAccessible =
                  isMemberOnlyRate(rate) && isReady && isAuthenticated && !isRateAccessible(rate);

                const onDecrement = generateOnDecrementAmount({
                  rateId: rate.id,
                  title: titleAndDescription.title,
                });

                const handleShopAddToCartWithValidation = async (
                  quantity: number,
                  childRateId: string | null
                ): Promise<boolean> => {
                  if (isMemberOnlyRate(rate) && !isReady) {
                    return false;
                  }
                  if (isMemberOnlyRate(rate) && !isAuthenticated) {
                    setMemberLoginDialogOpen(true);
                    return false;
                  }
                  if (usesInlineShopPwyc && pwycMin > 0 && parentTicketIndex >= 0) {
                    const currentPwycAmount = form.getValues(`ticketsPurchased.${parentTicketIndex}.pwycAmount`);
                    const currentRowQuantity =
                      form.getValues(`ticketsPurchased.${parentTicketIndex}.purchasedNumber`) ?? 0;
                    const unitAmount =
                      currentPwycAmount !== null && currentPwycAmount !== undefined
                        ? deriveDisplayUnitAmount(currentPwycAmount, currentRowQuantity)
                        : null;
                    if (unitAmount === null || unitAmount < pwycMin) {
                      form.setError(`ticketsPurchased.${parentTicketIndex}.pwycAmount`, {
                        type: "min",
                        message:
                          unitAmount === null
                            ? t("ticketing", "payWhatYouCanInputRequired")
                            : t("ticketing", "payWhatYouCanInputMinError_OneTicket", {
                                amount: formatCurrencyFromCents(pwycMin, { isoLocale, currency: formCurrency }),
                              }),
                      });
                      return false;
                    }
                  }
                  if (rateQuestions.length > 0) {
                    const allPendingAnswers = form.getValues("shopPendingAnswers") ?? {};
                    const pendingAnswersForRate = allPendingAnswers[rate.id] ?? [];
                    const fieldPaths: string[] = pendingAnswersForRate.flatMap((_, itemIndex) =>
                      rateQuestions.flatMap((question, answerIndex) => {
                        const basePath = `shopPendingAnswers.${rate.id}.${itemIndex}.answers.${answerIndex}`;
                        if (question.type === "Multiple" || question.type === "MultipleCheckboxes") {
                          return [`${basePath}.choiceIds`];
                        }
                        return [`${basePath}.answers.0`];
                      })
                    );
                    if (fieldPaths.length > 0) {
                      const isValid = await form.trigger(fieldPaths as never[]);
                      if (!isValid) {
                        return false;
                      }
                    }
                  }
                  handleShopAddToCart(rate, quantity, childRateId);
                  return true;
                };

                const additionalQuestions = () => {
                  if (isQuestionsPreviewStep && rateQuestions.length > 0 && isShopForm) {
                    return (
                      <ShopAdditionalQuestionsBlock
                        rateId={rate.id}
                        rateQuestions={rateQuestions}
                        shopQuantity={2}
                        rateIndex={index}
                        form={form}
                      />
                    );
                  }

                  if (rateQuestions.length === 0 || !isActive) {
                    return null;
                  }

                  // In variant-mode shops, all rate questions (with or without variants) are collected
                  // through the shop pending-answers flow before add-to-cart. The outer block would
                  // otherwise re-render them after add, making them appear to persist.
                  if (isShopForm) {
                    return null;
                  }

                  if (memberInfo.isEnabled) {
                    if (rate.groupMembershipMaxMembers && !isQuestionsPreviewStep) {
                      return (
                        <GroupMembershipMembers
                          rateId={rate.id}
                          title={titleAndDescription.title}
                          maximumMembers={rate.groupMembershipMaxMembers}
                          groupCount={purchasedNumber}
                          questions={rateQuestions}
                          participants={customAnswersOfParticipantsFieldArray}
                          onAddGroup={onIncrement}
                          canAddGroup={purchasedNumber < maximumPurchasableTicketsForRate}
                          canAddMember={remainingMemberCapacity > 0}
                        />
                      );
                    }
                    return (
                      <MemberInfoQuestions
                        participantIndexes={participantsForRate.map((participant) =>
                          allParticipants.findIndex((field) => field.id === participant.id)
                        )}
                        questions={rateQuestions}
                        fieldIdPrefix={`rate-${index}`}
                        isPreview={isQuestionsGhostPreview}
                        isEditingQuestions={isQuestionsPreviewStep}
                      />
                    );
                  }

                  const isGroupTicket = (rate.attendeesByTicket || 1) > 1;

                  return (
                    <AdditionalQuestions withDividers={isGroupTicket} isPreview={isQuestionsGhostPreview}>
                      {(() => {
                        const Container = isGroupTicket ? AdditionalQuestionsForGroup : Fragment;

                        return Array.from({ length: purchasedNumber }).map((_, groupIndex) => (
                          <Container key={groupIndex}>
                            {(() => {
                              const participants = participantsForRate.slice(
                                groupIndex * (rate.attendeesByTicket || 1),
                                (groupIndex + 1) * (rate.attendeesByTicket || 1)
                              );

                              return participants.map((participant, participantIndex) => (
                                <AdditionalQuestionsForTicket
                                  key={participantIndex}
                                  itemType={itemType}
                                  isEntry={usesEntryTerminology(formCategory)}
                                  purchasedAmount={purchasedNumber}
                                  groupNumber={groupIndex + 1}
                                  participantNumber={participantIndex + 1}
                                  isPartOfGroupTicket={isGroupTicket}
                                >
                                  {rateQuestions.map((question, answerIndex) => {
                                    const participantFieldIndex = allParticipants.findIndex(
                                      (f) => f.id === participant.id
                                    );
                                    return (
                                      <QuestionField
                                        key={answerIndex}
                                        name={`customAnswersOfParticipants.${participantFieldIndex}.answers.${answerIndex}`}
                                        question={question}
                                        id={`rate-${index}-group-${groupIndex}-participant-${participantIndex}-question-${answerIndex}`}
                                      />
                                    );
                                  })}
                                </AdditionalQuestionsForTicket>
                              ));
                            })()}
                          </Container>
                        ));
                      })()}
                    </AdditionalQuestions>
                  );
                };

                if (isAuction) {
                  return (
                    <AuctionRateItemContainer
                      key={rate.id}
                      rate={rate}
                      control={form.control}
                      formCurrency={formCurrency}
                      maximumPurchasableTicketsForRate={maximumPurchasableTicketsForRate}
                      ticketingIsClosed={ticketingIsClosed}
                      onClickRate={openDrawerForRate}
                    />
                  );
                }

                return (
                  <Fragment key={rate.id}>
                    <RateCard
                      id={tileId}
                      rate={rate}
                      formCurrency={formCurrency}
                      formCategory={formCategory}
                      purchasedNumber={purchasedNumber}
                      maximumPurchasableTicketsForRate={maximumPurchasableTicketsForRate}
                      maximumPurchasableTicketsForRateAndEventCapacity={
                        maximumPurchasableTicketsForRateAndEventCapacity
                      }
                      ticketingIsClosed={ticketingIsClosed}
                      active={isActive}
                      isSoldOut={isRateSoldOut}
                      highlightScheduleStatus={itemType === "membership"}
                      itemType={itemType}
                      isPreview={isPreview}
                      form={form}
                      actionsConfig={{
                        onIncrement,
                        onDecrement,
                        isMemberOnlyRate: isMemberOnlyRate(rate),
                        isMemberOnlyAndNotAccessible,
                        isReady,
                        rateSoldOutReason,
                        isP2PLinkedEvent,
                      }}
                      additionalQuestions={{ node: additionalQuestions() }}
                      duplicateWarning={duplicateWarning}
                      onAddToCart={isShopForm ? handleShopAddToCartWithValidation : undefined}
                      shopAdditionalQuestionsBuilder={
                        isShopForm && rateQuestions.length > 0
                          ? (shopQuantity) => (
                              <ShopAdditionalQuestionsBlock
                                rateId={rate.id}
                                rateQuestions={rateQuestions}
                                shopQuantity={shopQuantity}
                                rateIndex={index}
                                form={form}
                              />
                            )
                          : undefined
                      }
                      amountInput={
                        isPayWhatYouCan ? (
                          usesInlineShopPwyc ? (
                            <ShopPayWhatYouCanInput
                              parentRate={rate}
                              parentTicketIndex={parentTicketIndex}
                              cartQuantity={shopCartQuantity}
                              minimumAmountPerTicket={rate.payWhatYouCanMinimumAmount}
                              suggestedAmountPerTicket={rate.payWhatYouCanSuggestedAmount}
                              disabled={isPwycCartLocked}
                              lockedTooltipText={isPwycCartLocked ? t("ticketing", "shopPwycInputLockedTooltip") : null}
                              form={form}
                              formCurrency={formCurrency}
                            />
                          ) : isActive ? (
                            <PayWhatYouCanInput
                              rateId={rate.id}
                              rates={allRates}
                              minimumAmountPerTicket={rate.payWhatYouCanMinimumAmount}
                              suggestedAmountPerTicket={rate.payWhatYouCanSuggestedAmount}
                              form={form}
                              formCurrency={formCurrency}
                              purchasedNumber={purchasedNumber}
                              isGroupTicket={(rate.attendeesByTicket || 1) > 1}
                              formCategory={formCategory}
                            />
                          ) : undefined
                        ) : undefined
                      }
                    />
                  </Fragment>
                );
              })}
            </InfiniteScroll>
          </Stack>
        </Box>
      </Stack>
      <MemberLoginDialog
        open={memberLoginDialogOpen}
        onClose={() => setMemberLoginDialogOpen(false)}
        organizationId={formObject.organizationId ?? ""}
        ticketingId={ticketing.id}
        locale={locale}
      />
      {isAuction && auctionDrawerSelectedRate && (
        <AuctionDetailDrawer
          open={isAuctionDrawerOpen}
          onClose={closeAuctionDrawer}
          onGoToCheckout={handleGoToCheckout}
          rate={auctionDrawerSelectedRate}
          formCurrency={formCurrency}
          form={form}
          questions={questions}
          auctionEndDateUtc={selectedOccurrenceWithRates?.occurrence?.endUtc}
          onNext={goToNextAuctionItem}
          onPrevious={goToPreviousAuctionItem}
          hasNext={hasNextAuctionItem}
          hasPrevious={hasPreviousAuctionItem}
        />
      )}
    </>
  );
};
